import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
const toAbs = (p) => (p.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + p : API + p);
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Trash2, Upload, ZoomIn, ZoomOut, ExternalLink, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * ReceiptPreview — modal that shows a receipt image (or PDF) large, with
 * pinch/wheel zoom, download, replace, and delete controls.
 *
 * Props:
 *   expense: { id, receipt_file_id, receipt_url, receipt_content_type, vendor, amount }
 *   open, onOpenChange
 *   onReplaced(newExpense), onDeleted(expenseId)  — parent list refresh
 */
export default function ReceiptPreview({ expense, open, onOpenChange, onReplaced, onDeleted }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const replaceInputRef = useRef(null);

  useEffect(() => {
    if (!open || !expense?.receipt_file_id) return;
    setZoom(1); setPan({ x: 0, y: 0 });
    // Fetch fresh signed URL (in case the one from list is stale)
    (async () => {
      try {
        const { data } = await api.get(`/uploads/${expense.receipt_file_id}/signed-url`);
        setSignedUrl(toAbs(data.signed_url));
      } catch {
        setSignedUrl(expense.receipt_url ? toAbs(expense.receipt_url) : null);
      }
    })();
  }, [open, expense?.receipt_file_id]);

  if (!expense) return null;

  const isPdf = (expense.receipt_content_type || "").includes("pdf") || (signedUrl || "").toLowerCase().includes(".pdf");
  const src = signedUrl || (expense.receipt_url ? toAbs(expense.receipt_url) : null);

  const download = async () => {
    if (!src) return;
    // Trigger browser download by opening the signed URL in a new tab.
    const a = document.createElement("a");
    a.href = src;
    a.download = `receipt-${expense.id}`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openFullSize = () => {
    if (!src) return;
    window.open(src, "_blank", "noopener,noreferrer");
  };

  const doReplace = async (file) => {
    if (!file) return;
    setReplacing(true);
    try {
      // Upload the new receipt
      const b = await api.get("/business");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("resource_type", "business");
      fd.append("resource_id", b.data.id);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Update the expense with the new receipt id
      const patchPayload = {
        date: expense.date, vendor: expense.vendor, amount: expense.amount,
        category_id: expense.category_id, category_name: expense.category_name,
        payment_method: expense.payment_method, description: expense.description,
        tax_deductible: expense.tax_deductible, receipt_file_id: up.id,
      };
      const { data } = await api.put(`/expenses/${expense.id}`, patchPayload);
      toast.success("Receipt replaced");
      onReplaced?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replace failed");
    } finally { setReplacing(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Remove this receipt? The expense itself will stay.")) return;
    try {
      const patchPayload = {
        date: expense.date, vendor: expense.vendor, amount: expense.amount,
        category_id: expense.category_id, category_name: expense.category_name,
        payment_method: expense.payment_method, description: expense.description,
        tax_deductible: expense.tax_deductible, receipt_file_id: null,
      };
      const { data } = await api.put(`/expenses/${expense.id}`, patchPayload);
      toast.success("Receipt removed");
      onReplaced?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const onWheel = (e) => {
    if (isPdf) return;
    e.preventDefault();
    const next = Math.min(4, Math.max(1, zoom + (e.deltaY < 0 ? 0.15 : -0.15)));
    setZoom(next);
    if (next === 1) setPan({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onMouseMove = (e) => {
    if (!dragStart) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onMouseUp = () => setDragStart(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="receipt-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100 flex-row items-center justify-between gap-3">
          <div>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">Receipt · {expense.vendor || "Expense"}</DialogTitle>
            <div className="text-xs text-zinc-500 mt-0.5">${Number(expense.amount || 0).toFixed(2)} · {(expense.date || "").slice(0, 10)}</div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {!isPdf && (
              <>
                <Button size="sm" variant="outline" onClick={() => setZoom((z) => Math.max(1, z - 0.25))} data-testid="receipt-zoom-out-btn" disabled={zoom <= 1}><ZoomOut className="w-4 h-4"/></Button>
                <div className="text-[10px] font-mono-accent text-zinc-500 px-1 min-w-[36px] text-center">{Math.round(zoom * 100)}%</div>
                <Button size="sm" variant="outline" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} data-testid="receipt-zoom-in-btn" disabled={zoom >= 4}><ZoomIn className="w-4 h-4"/></Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={openFullSize} data-testid="receipt-fullsize-btn" title="Open in new tab"><ExternalLink className="w-4 h-4"/></Button>
            <Button size="sm" variant="outline" onClick={download} data-testid="receipt-download-btn" title="Download"><Download className="w-4 h-4"/></Button>
            <Button size="sm" variant="outline" onClick={() => replaceInputRef.current?.click()} data-testid="receipt-replace-btn" title="Replace" disabled={replacing}>
              {replacing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
            </Button>
            <input ref={replaceInputRef} type="file" accept="image/*,application/pdf" capture="environment" onChange={(e) => doReplace(e.target.files?.[0])} className="hidden" data-testid="receipt-replace-input"/>
            <Button size="sm" variant="outline" onClick={doDelete} className="text-rose-600" data-testid="receipt-delete-btn" title="Remove"><Trash2 className="w-4 h-4"/></Button>
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="receipt-close-btn"><X className="w-4 h-4"/></Button>
          </div>
        </DialogHeader>
        <div
          className="bg-zinc-900 flex items-center justify-center overflow-hidden select-none"
          style={{ height: "70vh", cursor: zoom > 1 ? (dragStart ? "grabbing" : "grab") : "default" }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {!src ? (
            <div className="text-zinc-500">No receipt file.</div>
          ) : isPdf ? (
            <iframe src={src} title="receipt-pdf" className="w-full h-full bg-white" data-testid="receipt-pdf-iframe"/>
          ) : (
            <img
              src={src}
              alt="receipt"
              draggable={false}
              className="max-w-full max-h-full object-contain transition-transform"
              style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center" }}
              data-testid="receipt-preview-img"
            />
          )}
        </div>
        <div className="p-3 bg-zinc-50 border-t border-zinc-100 text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 text-center flex items-center justify-center gap-3">
          {isPdf ? <FileText className="w-3 h-3"/> : null}
          {isPdf ? "PDF · scroll inside preview to page" : "Scroll to zoom · Drag to pan"}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ReceiptThumbnail — compact clickable preview for lists.
 */
export function ReceiptThumbnail({ expense, size = 40, onOpen }) {
  const [failed, setFailed] = useState(false);
  if (!expense?.receipt_file_id || failed) {
    return (
      <button
        type="button"
        onClick={onOpen}
        disabled={!expense?.receipt_file_id}
        className={`rounded border border-dashed ${expense?.receipt_file_id ? "border-zinc-300 hover:border-[#FF5A00]" : "border-zinc-200 cursor-default"} bg-zinc-50 flex items-center justify-center text-[9px] text-zinc-400 font-mono-accent uppercase tracking-widest`}
        style={{ width: size, height: size }}
        data-testid={`receipt-thumb-empty-${expense?.id}`}
      >
        {expense?.receipt_file_id ? "PDF" : "—"}
      </button>
    );
  }
  const isPdf = (expense.receipt_content_type || "").includes("pdf");
  const src = expense.receipt_url ? toAbs(expense.receipt_url) : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded border border-zinc-200 overflow-hidden hover:border-[#FF5A00] transition bg-white group relative"
      style={{ width: size, height: size }}
      data-testid={`receipt-thumb-${expense.id}`}
    >
      {isPdf || !src ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-rose-50 text-rose-600">
          <FileText className="w-4 h-4"/>
          <span className="text-[8px] font-mono-accent uppercase tracking-widest">PDF</span>
        </div>
      ) : (
        <img src={src} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)}/>
      )}
    </button>
  );
}
